import { AttrTypeName } from "src/types/AttrType";
import { ZAttribute } from "src/types/ZAttribute";
import dayjs from "dayjs";
import { emptyValueMarker } from "src/common/emptyValueMarker";
import { ZEntityFilterValue } from "./AttsTree/types";

// Константы для работы с пустыми значениями
const isEmptyValue = (val?: string) => val === emptyValueMarker;

/**
 * Сортировщик по умолчанию - по имени
 */
const defaultSorter = (a: ZEntityFilterValue, b: ZEntityFilterValue) =>
  (a.name || "").localeCompare(b.name || "");

/**
 * Сортировщик для дат - сравнивает как ISO строки
 */
const dateSorter = (a: ZEntityFilterValue, b: ZEntityFilterValue) => {
  const aDate = dayjs(a.toSearch[0]);
  const bDate = dayjs(b.toSearch[0]);

  if (aDate.isValid() && bDate.isValid()) {
    return aDate.valueOf() - bDate.valueOf();
  }

  // Обработка невалидных дат
  return String(a.toSearch[0]).localeCompare(String(b.toSearch[0]));
};

/**
 * Сортировщик для булевых значений - true идут первыми
 */
const booleanSorter = (a: ZEntityFilterValue, b: ZEntityFilterValue) => {
  const aBool = String(a.toSearch?.[0]).toLowerCase() === "true";
  const bBool = String(b.toSearch?.[0]).toLowerCase() === "true";

  if (aBool === bBool) return 0;
  return aBool ? -1 : 1;
};

// Маппинг типов атрибутов на соответствующие сортировщики
const typeSorters: Partial<
  Record<AttrTypeName, (a: ZEntityFilterValue, b: ZEntityFilterValue) => number>
> = {
  [AttrTypeName.boolean]: booleanSorter,
  [AttrTypeName.date]: dateSorter,
};

/**
 * Сортирует значения фильтра дерева по типу атрибута
 * @param attr - атрибут для сортировки
 * @param values - значения для сортировки
 * @param typesMap - маппинг типов атрибутов
 * @returns отсортированный массив значений
 */
export const sortFilterTreeValues = (
  attr: ZAttribute,
  values: ZEntityFilterValue[],
  typesMap: Record<number, string>, // 8. Улучшенная типизация
) => {
  if (!values || values.length === 0) return [];

  const type = typesMap[attr.valueType] as AttrTypeName;
  if (!type) return values;

  const [empty, nonEmpty] = values.reduce<
    [ZEntityFilterValue[], ZEntityFilterValue[]]
  >(
    (acc, val) => {
      acc[isEmptyValue(val.toSearch[0]) ? 0 : 1].push(val);
      return acc;
    },
    [[], []],
  );

  const sorter = typeSorters[type] || defaultSorter;

  return [...nonEmpty.sort(sorter), ...empty];
};
